import { ENV } from "../../../../utils/constants";

export function getSummaryResponse(result) {
  return result?.summary || result?.response || result || {};
}

export function getProfitSummaryResponse(result) {
  const months =
    result?.months ||
    result?.response?.months ||
    result?.response ||
    result ||
    [];

  return Array.isArray(months) ? months : [];
}

export function getExistencesResponse(result) {
  const response = result?.existences || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response;
  }

  if (Array.isArray(response)) {
    return { docs: response, totalDocs: response.length };
  }

  return { docs: [], totalDocs: 0 };
}

export function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const value = String(imagePath);

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  return `${ENV.SERVER_HOST}/${value
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")}`;
}

function getProductImage(product) {
  const images = Array.isArray(product?.images) ? product.images : [];
  const coverImage = images.find((image) => image?.isCover) || images[0];

  return getImageUrl(coverImage?.path);
}

export function flattenExistences(products = []) {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.flatMap((product) => {
    const variants = Array.isArray(product?.variants)
      ? product.variants
      : [];

    return variants
      .filter((variant) => variant?.enabled !== false)
      .map((variant) => ({
        productId: String(product._id),
        variantId: String(variant._id),
        productName: product.name || "",
        productImage: getProductImage(product),
        sku: variant.sku || "—",
        stock: Number(variant.stock) || 0,
        price: Number(variant.price) || 0,
        value: (Number(variant.stock) || 0) * (Number(variant.price) || 0),
      }));
  });
}

/*
 * "Productos por categoría" ya no se calcula en el navegador a partir de
 * la primera página de existencias (eso dejaba afuera categorías cuyos
 * productos quedaban más allá del límite de esa página). Ahora viene
 * directo del resumen del servidor, calculado sobre todos los productos.
 */
export function getCategoryBreakdown(summary = {}) {
  return Array.isArray(summary?.categoryBreakdown)
    ? summary.categoryBreakdown
    : [];
}

const MONTH_LABELS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

export function buildYearlyProfitSeries(months = [], year) {
  const byMonth = new Map(
    (Array.isArray(months) ? months : []).map((entry) => [
      Number(entry.month),
      entry,
    ])
  );

  return Array.from({ length: 12 }, (_, index) => {
    const month = index + 1;
    const entry = byMonth.get(month);

    return {
      key: `${year}-${month}`,
      label: MONTH_LABELS[index],
      year,
      month,
      unitsSold: Number(entry?.unitsSold) || 0,
      revenue: Number(entry?.revenue) || 0,
      cost: Number(entry?.cost) || 0,
      profit: Number(entry?.profit) || 0,
    };
  });
}

export function getGreeting() {
  const hour = new Date().getHours();

  if (hour < 12) {
    return "Buenos días";
  }

  if (hour < 19) {
    return "Buenas tardes";
  }

  return "Buenas noches";
}

export function getFirstName(user) {
  const source = String(user?.name || user?.email || "").trim();

  if (!source) {
    return "";
  }

  return source.split(" ")[0].split("@")[0];
}

export function buildTopProductsByStock(rows = [], limit = 4) {
  return [...rows]
    .filter((row) => row.stock > 0)
    .sort((first, second) => second.stock - first.stock)
    .slice(0, limit)
    .map((row, index) => ({
      id: `${row.productId}-${row.variantId}`,
      position: index + 1,
      name: row.productName,
      sku: row.sku,
      stock: row.stock,
      value: row.value,
      image: row.productImage,
    }));
}

export function formatCurrency(value) {
  return `Q ${(Number(value) || 0).toLocaleString("es-GT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function getSeriesTotals(series = []) {
  return (Array.isArray(series) ? series : []).reduce(
    (accumulated, item) => ({
      unitsSold: accumulated.unitsSold + (Number(item.unitsSold) || 0),
      revenue: accumulated.revenue + (Number(item.revenue) || 0),
      cost: accumulated.cost + (Number(item.cost) || 0),
      profit: accumulated.profit + (Number(item.profit) || 0),
    }),
    { unitsSold: 0, revenue: 0, cost: 0, profit: 0 }
  );
}

export function buildDashboardMetrics({
  summary = {},
  periodTotals = { cost: 0, profit: 0 },
  year,
} = {}) {
  const inventoryValue = Number(summary.inventoryValue) || 0;
  const totalStock = Number(summary.totalStock) || 0;
  const invested = Number(periodTotals.cost) || 0;
  const profit = Number(periodTotals.profit) || 0;

  const isCurrentYear = !year || year >= new Date().getFullYear();
  const inventoryMoment = isCurrentYear ? "hoy" : `cierre de ${year}`;

  return [
    {
      id: "invested",
      title: "Invertido",
      value: formatCurrency(invested),
      description: "costo de lo vendido en el año elegido",
      icon: "shopping bag",
      type: "primary",
    },
    {
      id: "profit",
      title: "Ganancia",
      value: formatCurrency(profit),
      description: "ingresos - costo del año elegido",
      icon: profit >= 0 ? "chart line" : "exclamation triangle",
      type: profit >= 0 ? "success" : "danger",
    },
    {
      id: "inventory-value",
      title: "Valor de inventario",
      value: formatCurrency(inventoryValue),
      description: `al último costo de compra (${inventoryMoment})`,
      icon: "money bill alternate outline",
      type: "success",
    },
    {
      id: "available-pieces",
      title: "Piezas disponibles",
      value: String(totalStock),
      description: `listas para vender (${inventoryMoment})`,
      icon: "gem",
      type: "primary",
    },
  ];
}

export function getYearOptions(referenceDate = new Date(), span = 5) {
  const currentYear = referenceDate.getFullYear();

  return Array.from({ length: span }, (_, index) => currentYear - index);
}

function getAttributeName(attribute) {
  if (attribute?.schema && typeof attribute.schema === "object") {
    return attribute.schema.name || "";
  }

  return attribute?.name || "";
}

function getAttributeValue(attribute) {
  if (
    attribute?.classification &&
    typeof attribute.classification === "object"
  ) {
    return (
      attribute.classification.name ||
      attribute.classification.value ||
      attribute.value ||
      ""
    );
  }

  return attribute?.label || attribute?.value || "";
}

function getVariantLabel(attributes) {
  const list = Array.isArray(attributes) ? attributes : [];

  if (list.length === 0) {
    return "Variante única";
  }

  return list
    .map((attribute) => {
      const name = getAttributeName(attribute);
      const value = getAttributeValue(attribute);

      if (name && value) {
        return `${name}: ${value}`;
      }

      return value || name;
    })
    .filter(Boolean)
    .join(" · ");
}

export function getYearlySalesDetailResponse(result) {
  const body = result?.response || result || {};

  const items = Array.isArray(body.items)
    ? body.items
    : Array.isArray(result?.items)
    ? result.items
    : [];

  const rawTotals = body.totals || result?.totals || {};

  const totals = {
    unitsSold: Number(rawTotals.unitsSold) || 0,
    revenue: Number(rawTotals.revenue) || 0,
    cost: Number(rawTotals.cost) || 0,
    profit: Number(rawTotals.profit) || 0,
  };

  return { items, totals };
}

function escapeCsvValue(value) {
  const text = String(value ?? "");

  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function buildCsvRow(values) {
  return values.map(escapeCsvValue).join(",");
}

export function buildYearlySalesCsv(items = [], totals = {}, year) {
  const rows = [
    buildCsvRow([`Resumen ${year}`]),
    buildCsvRow([
      "Producto",
      "SKU",
      "Variante",
      "Cantidad",
      "Ingresos",
      "Costo",
      "Ganancia",
    ]),
  ];

  items.forEach((item) => {
    rows.push(
      buildCsvRow([
        item.productName || "",
        item.sku || "",
        getVariantLabel(item.attributes),
        item.quantity || 0,
        (Number(item.revenue) || 0).toFixed(2),
        (Number(item.cost) || 0).toFixed(2),
        (Number(item.profit) || 0).toFixed(2),
      ])
    );
  });

  rows.push(buildCsvRow([]));
  rows.push(
    buildCsvRow([
      "Total",
      "",
      "",
      totals.unitsSold || 0,
      (Number(totals.revenue) || 0).toFixed(2),
      (Number(totals.cost) || 0).toFixed(2),
      (Number(totals.profit) || 0).toFixed(2),
    ])
  );

  // ← BOM para que Excel abra los acentos/Q correctamente en Windows.
  return `﻿${rows.join("\r\n")}`;
}

export function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

export function buildExportFilename(year) {
  return `resumen-ventas-${year}.csv`;
}
